import { useEffect, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calendar, MapPin, Clock, ArrowRight, ChevronLeft, ChevronRight, X, Users, Send, List, LayoutGrid } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import Seo from "../components/Seo";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}
function formatTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d) || iso.length <= 10) return "";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [rsvpEvent, setRsvpEvent] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/events");
        setEvents(data);
      } catch (e) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const today = new Date(new Date().toDateString());
  const upcoming = events.filter((e) => new Date(e.event_date) >= today);
  const past = events.filter((e) => new Date(e.event_date) < today);

  return (
    <main id="main-content" className="bg-[#050505] text-white" data-testid="events-page">
      <Seo
        title="Events"
        description="Worship nights, outreach, bible studies, and special gatherings at The Push Center. Mark your calendar and RSVP."
        path="/events"
      />

      <section className="relative pt-20 pb-12 lg:pt-32 lg:pb-16 overflow-hidden">
        <div className="absolute inset-0 lightning-grid opacity-30" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-10">
          <div className="text-[11px] uppercase tracking-[0.4em] text-electric font-semibold mb-5">
            What's Happening
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
            Events at <span className="italic text-electric text-pulse">The Push.</span>
          </h1>
          <p className="mt-7 text-lg text-zinc-300 max-w-2xl">
            Worship nights, outreach, bible studies, and special gatherings. RSVP to save your seat.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-10 pb-24">
        <div className="flex items-center justify-between mb-8">
          <div className="text-[10px] uppercase tracking-[0.3em] text-electric font-semibold">
            {view === "list" ? "Upcoming & Past" : "Monthly Calendar"}
          </div>
          <div className="flex gap-1 bg-[#0a0a0a] border border-white/10 rounded-full p-1">
            <button
              onClick={() => setView("list")}
              className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition flex items-center gap-1.5 ${
                view === "list" ? "bg-electric text-black" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="events-view-list"
            >
              <List className="w-3.5 h-3.5" /> List
            </button>
            <button
              onClick={() => setView("calendar")}
              className={`px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition flex items-center gap-1.5 ${
                view === "calendar" ? "bg-electric text-black" : "text-zinc-400 hover:text-white"
              }`}
              data-testid="events-view-calendar"
            >
              <LayoutGrid className="w-3.5 h-3.5" /> Calendar
            </button>
          </div>
        </div>

        {loading ? (
          <div className="py-32 flex justify-center" data-testid="events-loading">
            <div className="w-10 h-10 border-2 border-electric border-t-transparent rounded-full animate-spin" />
          </div>
        ) : view === "calendar" ? (
          <CalendarView events={events} onRsvp={setRsvpEvent} />
        ) : upcoming.length === 0 && past.length === 0 ? (
          <div className="py-24 text-center" data-testid="events-empty">
            <Calendar className="w-12 h-12 text-electric mx-auto mb-5" />
            <h3 className="font-display font-bold text-2xl mb-3">No events scheduled yet</h3>
            <p className="text-zinc-400 max-w-md mx-auto">
              Check back soon — we're planning powerful gatherings for the season ahead.
            </p>
          </div>
        ) : (
          <>
            {upcoming.length > 0 && (
              <div className="mb-16">
                <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-6">Upcoming</div>
                <div className="grid lg:grid-cols-2 gap-5">
                  {upcoming.map((evt, i) => (
                    <motion.div
                      key={evt.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.45, delay: i * 0.08 }}
                      className="group relative rounded-2xl overflow-hidden border border-white/5 bg-[#0a0a0a] hover:border-electric/50 transition-all"
                      data-testid={`event-card-${i}`}
                    >
                      {evt.image_url ? (
                        <div className="aspect-[16/9] overflow-hidden">
                          <img src={evt.image_url} alt={evt.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                        </div>
                      ) : (
                        <div className="aspect-[16/9] bg-gradient-to-br from-electric/20 via-[#0a0a0a] to-[#0a0a0a] flex items-center justify-center">
                          <Calendar className="w-12 h-12 text-electric opacity-40" />
                        </div>
                      )}
                      <div className="p-7">
                        <div className="flex items-center gap-4 text-xs text-electric mb-3 font-bold uppercase tracking-wider">
                          <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{formatDate(evt.event_date)}</span>
                          {formatTime(evt.event_date) && (
                            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{formatTime(evt.event_date)}</span>
                          )}
                        </div>
                        <h3 className="font-display font-black text-2xl tracking-tight mb-3">{evt.title}</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed mb-4 line-clamp-3">{evt.description}</p>
                        {evt.location && (
                          <div className="flex items-center gap-1.5 text-sm text-zinc-300 mb-5">
                            <MapPin className="w-3.5 h-3.5 text-electric" /> {evt.location}
                          </div>
                        )}
                        <button
                          onClick={() => setRsvpEvent(evt)}
                          className="inline-flex items-center gap-2 bg-electric text-black px-5 py-2.5 rounded-full font-bold text-sm hover:bg-[#4ade80] transition-all"
                          data-testid={`rsvp-btn-${evt.id}`}
                        >
                          <Users className="w-4 h-4" /> RSVP
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {past.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-6">Past Events</div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {past.slice(0, 6).map((evt, i) => (
                    <div key={evt.id} className="p-6 rounded-xl border border-white/5 bg-[#0a0a0a] opacity-60" data-testid={`past-event-${i}`}>
                      <div className="text-xs text-zinc-500 mb-2">{formatDate(evt.event_date)}</div>
                      <div className="font-display font-bold text-lg">{evt.title}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </section>

      <AnimatePresence>
        {rsvpEvent && <RSVPModal event={rsvpEvent} onClose={() => setRsvpEvent(null)} />}
      </AnimatePresence>
    </main>
  );
}

function CalendarView({ events, onRsvp }) {
  const [month, setMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d;
  });

  const cells = useMemo(() => {
    const year = month.getFullYear();
    const m = month.getMonth();
    const firstDay = new Date(year, m, 1).getDay();
    const daysInMonth = new Date(year, m + 1, 0).getDate();
    const result = [];
    for (let i = 0; i < firstDay; i++) result.push({ blank: true });
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, m, d);
      const eventsOnDay = events.filter((e) => {
        const ed = new Date(e.event_date);
        return ed.getFullYear() === year && ed.getMonth() === m && ed.getDate() === d;
      });
      result.push({ date, day: d, events: eventsOnDay });
    }
    return result;
  }, [month, events]);

  const monthLabel = month.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const today = new Date();

  return (
    <div data-testid="calendar-view">
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
          className="w-10 h-10 rounded-full border border-white/10 hover:border-electric text-zinc-300 hover:text-electric flex items-center justify-center transition"
          data-testid="cal-prev"
          aria-label="Previous month"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <h2 className="font-display font-black text-2xl lg:text-3xl tracking-tight">{monthLabel}</h2>
        <button
          onClick={() => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
          className="w-10 h-10 rounded-full border border-white/10 hover:border-electric text-zinc-300 hover:text-electric flex items-center justify-center transition"
          data-testid="cal-next"
          aria-label="Next month"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="text-center py-2">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((cell, i) => {
          if (cell.blank) return <div key={i} />;
          const isToday =
            cell.date.toDateString() === today.toDateString();
          const hasEvent = cell.events.length > 0;
          return (
            <div
              key={i}
              className={`min-h-[90px] lg:min-h-[120px] p-2 rounded-lg border transition ${
                isToday ? "border-electric/60 bg-electric/5" : "border-white/5 bg-[#0a0a0a]"
              } ${hasEvent ? "ring-1 ring-electric/30" : ""}`}
              data-testid={`cal-day-${cell.day}`}
            >
              <div className={`text-xs font-bold mb-1.5 ${isToday ? "text-electric" : "text-zinc-400"}`}>{cell.day}</div>
              <div className="space-y-1">
                {cell.events.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => onRsvp(e)}
                    className="w-full text-left px-1.5 py-1 rounded bg-electric/15 hover:bg-electric/25 text-electric text-[10px] lg:text-[11px] font-semibold truncate transition"
                    data-testid={`cal-event-${e.id}`}
                    title={e.title}
                  >
                    {formatTime(e.event_date) && <span className="text-zinc-400 mr-1">{formatTime(e.event_date)}</span>}
                    {e.title}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RSVPModal({ event, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", guests: 1, notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, event_id: event.id, guests: Number(form.guests) || 1 };
      if (!payload.phone) delete payload.phone;
      if (!payload.notes) delete payload.notes;
      await api.post(`/events/${event.id}/rsvp`, payload);
      setDone(true);
      toast.success("RSVP confirmed! See you there.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "RSVP failed");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full px-4 py-3 rounded-lg bg-[#050505] border border-white/10 text-white placeholder-zinc-600 focus:outline-none focus:border-electric focus:ring-1 focus:ring-electric/30 transition";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center px-4 py-8 overflow-y-auto"
      onClick={onClose}
      data-testid="rsvp-modal"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0a0a0a] border border-electric/20 rounded-2xl max-w-lg w-full p-7 lg:p-9 relative"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-zinc-400 hover:text-white" aria-label="Close" data-testid="rsvp-close">
          <X className="w-5 h-5" />
        </button>
        {done ? (
          <div className="text-center py-6" data-testid="rsvp-success">
            <div className="w-14 h-14 rounded-full bg-electric/20 flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-6 h-6 text-electric" />
            </div>
            <h3 className="font-display font-black text-2xl mb-3">You're in!</h3>
            <p className="text-zinc-400 mb-6">We've saved your seat for <strong className="text-white">{event.title}</strong>. See you {formatDate(event.event_date)}.</p>
            <button onClick={onClose} className="bg-electric text-black px-6 py-3 rounded-full font-bold text-sm hover:bg-[#4ade80] transition" data-testid="rsvp-done">
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="text-[10px] uppercase tracking-[0.3em] text-electric mb-2">RSVP</div>
            <h3 className="font-display font-black text-2xl tracking-tight mb-1">{event.title}</h3>
            <div className="text-sm text-zinc-400 mb-6">{formatDate(event.event_date)}{formatTime(event.event_date) && ` · ${formatTime(event.event_date)}`}</div>

            <form onSubmit={submit} className="space-y-4" data-testid="rsvp-form">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Your Name</label>
                <input className={inputCls} required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rsvp-name" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Email</label>
                <input type="email" className={inputCls} required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="rsvp-email" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Phone (optional)</label>
                  <input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="rsvp-phone" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5"># Guests</label>
                  <input type="number" min="1" max="20" className={inputCls} value={form.guests} onChange={(e) => setForm({ ...form, guests: e.target.value })} data-testid="rsvp-guests" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-400 font-bold block mb-1.5">Notes (optional)</label>
                <textarea rows={3} className={inputCls} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Anything we should know?" data-testid="rsvp-notes" />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 bg-electric text-black px-6 py-3.5 rounded-full font-bold tracking-wide hover:bg-[#4ade80] transition disabled:opacity-60"
                data-testid="rsvp-submit"
              >
                <Send className="w-4 h-4" />
                {submitting ? "Submitting..." : "Confirm RSVP"}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </motion.div>
  );
}
